import { createApi } from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../utils/constants";
import { axiosBaseQuery } from "../baseQuery";

import configurationParser from "../parser/configurationParser";

import {
  ParameterType,
  ParameterTypeResponse,
  ManageScoreResponse,
  ManageScoreApiReq,
  ManageAward,
  ManageAwardResponse,
  TargetResponse,
  ManageScorePayload,
} from "../types/configuration";

export const configurationApis = createApi({
  reducerPath: "configurationApis",
  baseQuery: axiosBaseQuery({ baseUrl: API_BASE_URL }),
  tagTypes: ["MANAGE_SCORE", "TARGET", "MANAGE_AWARD", "PARAMETER_TYPE"],
  endpoints: (builder) => ({
    parameterType: builder.query<ParameterType[], void>({
      query: (params) => ({
        url: "/parameter-type",
        method: "GET",
        params: params,
      }),
      providesTags: ["PARAMETER_TYPE"],
      transformResponse: (res: ParameterTypeResponse) =>
        configurationParser.parameterType(res),
    }),

    manageScorePost: builder.mutation<any, ManageScorePayload>({
      query: (payload) => {
        return {
          url: "/manage-score",
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: ["MANAGE_SCORE"],
    }),

    manageScorePut: builder.mutation({
      query: ({ manageScoreId, payload }) => {
        return {
          url: `/manage-score`,
          method: "PUT",
          data: payload,
        };
      },
      invalidatesTags: ["MANAGE_SCORE"],
    }),

    manageScore: builder.query<any, ManageScoreApiReq | undefined>({
      query: (payload) => {
        return {
          url: "/manage-score",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["MANAGE_SCORE"],
      transformResponse: (res: ManageScoreResponse) =>
        configurationParser.manageScoreParser(res),
    }),

    getManageAward: builder.query({
      query: (payload: any) => {
        return {
          url: "/manage-award",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["MANAGE_AWARD"],
      transformResponse: (res: ManageAwardResponse) =>
        configurationParser.getManageAwardParser(res),
    }),

    manageAwardEdit: builder.mutation<
      ManageAwardResponse,
      { id: string; body: any }
    >({
      query: (payload) => {
        const { id, body } = payload;
        return {
          url: `/manage-award/${id}`,
          method: "PUT",
          data: body,
        };
      },
      invalidatesTags: ["MANAGE_AWARD"],
    }),

    manageAwardCreate: builder.mutation<ManageAwardResponse, { body: any }>({
      query: (payload) => {
        const { body } = payload;
        return {
          url: `/manage-award`,
          method: "POST",
          data: body,
        };
      },
      invalidatesTags: ["MANAGE_AWARD"],
    }),

    manageAwardDelete: builder.mutation<ManageAwardResponse, { id: string }>({
      query: (payload) => {
        const { id } = payload;
        return {
          url: `/manage-award/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["MANAGE_AWARD"],
    }),

    getTarget: builder.query({
      query: (payload: any) => {
        return {
          url: "/target",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["TARGET"],
      transformResponse: (res: TargetResponse) =>
        configurationParser.getTargetParser(res),
    }),

    getTargetDownload: builder.query({
      query: (payload: any) => {
        return {
          url: "/target",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["TARGET"],
      transformResponse: (res: TargetResponse) =>
        configurationParser.getTargetParser(res),
    }),

    createTarget: builder.mutation({
      query: (payload: any) => {
        return {
          url: "/target",
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: ["TARGET"],
    }),

    deleteTarget: builder.mutation({
      query: (targetID: string | null) => {
        return {
          url: `/target/delete/${targetID}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["TARGET"],
    }),

    getTargetParameter: builder.query({
      query: (payload: any) => {
        return {
          url: "/target/parameter",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["TARGET"],
      transformResponse: (res: any) =>
        configurationParser.targetParameterParser(res),
    }),

    editParameterType: builder.mutation({
      query: (payload) => {
        return {
          url: `/parameter-type`,
          method: "PUT",
          data: payload,
        };
      },
      invalidatesTags: ["PARAMETER_TYPE"],
    }),
  }),
});

export const {
  useManageScorePostMutation,
  useParameterTypeQuery,
  useManageScoreQuery,
  useGetManageAwardQuery,
  useManageAwardEditMutation,
  useManageAwardCreateMutation,
  useManageAwardDeleteMutation,
  useGetTargetQuery,
  useLazyGetTargetDownloadQuery,
  useManageScorePutMutation,
  useCreateTargetMutation,
  useGetTargetParameterQuery,
  useDeleteTargetMutation,
  useEditParameterTypeMutation,
} = configurationApis;
